/**
 * GameLogControls — the controls that drive the game log beside the chat
 * input. Wide layouts put them in a row above the input:
 *
 *   [ ALL | CHAT ]  ……  [filters] [grow]
 *
 * Portrait phones put them on the input's own line instead (gameLogControlParts:
 * the pill left of the input, filters and grow right of send).
 *
 *   ALL | CHAT  the log with its chat lines, or chat only. Unread chat badges
 *               it while ALL shows; tapping CHAT clears the badge (the lines
 *               still play their unread highlight).
 *   filters     opens the filter chips (GameLogFilterPanel); a dot marks
 *               active filters while it's closed. For CHAT the chips are
 *               players and sort (chat lines have no event kind).
 *   grow        grows the log to just under the header bar (GameLogSheet) and
 *               back.
 */
import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { useTheme } from '../../hooks/useTheme'
import { SegmentedToggle, type SegmentOption } from '../ui/SegmentedToggle'

export type GameLogView = 'all' | 'chat'

const VIEW_OPTIONS: readonly SegmentOption<GameLogView>[] = [
  { value: 'all', label: 'All', accessibilityLabel: 'Show chat and log', testID: 'chat-log-filter-all' },
  { value: 'chat', label: 'Chat', accessibilityLabel: 'Show only chat', testID: 'chat-log-filter-chat' },
]

const SIZE = 30

function IconToggle({
  icon,
  active,
  onPress,
  label,
  testID,
  dot = false,
}: {
  icon: React.ComponentProps<typeof MaterialCommunityIcons>['name']
  active: boolean
  onPress: () => void
  label: string
  testID: string
  dot?: boolean
}) {
  const { theme } = useTheme()
  const ink = theme === 'dark' ? '#e6e1c4' : '#18170f'
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityRole="switch"
      accessibilityLabel={label}
      accessibilityState={{ checked: active }}
      testID={testID}
      className={`${active ? 'bg-blood' : 'bg-paper'} border border-olive rounded-md items-center justify-center`}
      style={{ width: SIZE + 4, height: SIZE }}
    >
      <MaterialCommunityIcons name={icon} size={17} color={active ? '#fff' : ink} />
      {dot ? (
        <View
          pointerEvents="none"
          className="bg-blood border border-paper"
          style={{ position: 'absolute', top: -3, right: -3, width: 9, height: 9, borderRadius: 5 }}
        />
      ) : null}
    </TouchableOpacity>
  )
}

export interface GameLogControlsProps {
  view: GameLogView
  /** False until the game has a chat line: no ALL | CHAT toggle yet. */
  canChatOnly: boolean
  onViewChange: (view: GameLogView) => void
  unreadCount: number
  filtersOpen: boolean
  onFiltersOpenChange: (open: boolean) => void
  filtersActive: boolean
  grown: boolean
  /** Omit where the log can't grow. */
  onGrownChange?: (grown: boolean) => void
}

/** The controls as separate pieces, for layouts that place them themselves. */
export function gameLogControlParts({
  view,
  canChatOnly,
  onViewChange,
  unreadCount,
  filtersOpen,
  onFiltersOpenChange,
  filtersActive,
  grown,
  onGrownChange,
}: GameLogControlsProps) {
  return {
    viewToggle: canChatOnly ? (
      <SegmentedToggle
        testID="chat-log-filter"
        size={SIZE}
        minWidth={40}
        options={VIEW_OPTIONS}
        value={view}
        onChange={onViewChange}
        badgeCount={view === 'all' ? unreadCount : 0}
      />
    ) : null,
    filtersToggle: (
      <IconToggle
        icon="filter-variant"
        active={filtersOpen}
        onPress={() => onFiltersOpenChange(!filtersOpen)}
        label={filtersOpen ? 'Hide log filters' : filtersActive ? 'Show log filters (filters active)' : 'Show log filters'}
        testID="game-log-filters-toggle"
        dot={filtersActive && !filtersOpen}
      />
    ),
    growToggle: onGrownChange ? (
      <IconToggle
        icon={grown ? 'arrow-collapse-vertical' : 'arrow-expand-vertical'}
        active={grown}
        onPress={() => onGrownChange(!grown)}
        label={grown ? 'Shrink the log' : 'Grow the log'}
        testID="game-log-grow-toggle"
      />
    ) : null,
  }
}

/**
 * Wide layouts: the controls as a row above the chat input. `trailing` sits at
 * the far right, after filter and grow (the off-turn cards button on desktop).
 */
export function GameLogControls({ trailing, ...props }: GameLogControlsProps & { trailing?: React.ReactNode }) {
  const { viewToggle, filtersToggle, growToggle } = gameLogControlParts(props)
  return (
    <View testID="game-log-controls" className="flex-row items-center" style={{ gap: 8, paddingBottom: 6 }}>
      {viewToggle}
      <View style={{ flex: 1 }} />
      {filtersToggle}
      {growToggle}
      {trailing}
    </View>
  )
}
